<template>
  <div ref="scribbleRef" class="c-scribble w-full c-scribble--responsive">
    <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
    <EditableComponent
      v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :add="componentPermissions.canEdit"
      :duplicate="componentPermissions.canDuplicate"
      :excludeStylables="[ThemeComponentOptionEnum.PADDING]"
      :expired-content="fileNotFound && !fileRestricted"
      :remove="componentPermissions.canRemove"
      :restricted-content="fileRestricted"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :stylable="!usedInSectionId"
      @add="onAdd(id)"
      @asset-change="updateNodeDataForAsset"
      @content-change="updateNodeDataForContent"
      @duplicate="duplicate(id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="handleEditableComponentStyleEvent"
    >
      <div
        ref="scrollableContainerRef"
        class="overflow-y-hidden"
        :class="{
          'overflow-x-hidden': !shouldShowHorizontalScroll,
          'overflow-x-scroll': shouldShowHorizontalScroll,
        }"
      >
        <Draggable
          ghost-class="ghost-class"
          handle=".drag-handle"
          item-key="id"
          :model-value="children"
          @dragend="clearState"
          @dragleave="onDragLeave"
          @dragover="onDragOver"
          @dragstart="onDragStart"
          @drop.prevent.stop="onDrop"
        >
          <div
            ref="containerRef"
            class="scribble-container !relative overflow-hidden rounded-2 bg-primary6"
            :class="{ 'scribble-container--covered': fileNotFound || fileRestricted }"
            :style="containerStyle"
          >
            <div ref="dropPreviewRef" :style="dropPreviewStyle" />
            <template v-if="shouldShowEmptyState">
              <div
                class="flex justify-center items-center b-rounded-2 cursor-pointer w-full h-full pa-8"
                @mousedown.stop="onMouseDown($event, id)"
              >
                <CButton circle secondary size="large" strong>
                  <template #icon>
                    <CIcon icon="plus-large" size="16px" />
                  </template>
                </CButton>
              </div>
            </template>
            <template v-else>
              <CSlideViewer
                v-if="showSlideViewer"
                class="absolute top-0 left-0 w-full h-full"
                :class="{ 'z-0': pdfReady, '-z-1': !pdfReady }"
                :page-index="computedPageIndex"
                :pdf-url="contentFileUrl!"
                @pdf-loaded="onPdfLoaded"
              />
              <a
                v-else-if="shouldShowReplaceLink"
                class="absolute top-0 left-0 w-full h-full flex items-center justify-center text-primary underline z-10"
                :href="replaceHref"
              >
                Replace
              </a>
              <div v-else-if="fileRestricted || fileNotFound"></div>
              <CErrorFullScreen v-else-if="showError" v-bind="fileNotFoundErrorProps" />
              <div
                v-if="!shouldShowReplaceLink && filteredChildren.length"
                :class="{
                  'absolute top-0 left-0 w-full h-full z-2':
                    contentFileUrl || fileNotFound || assetFileNotFound || computedPageIndex === -1,
                }"
                :style="childrenContainerStyle"
              >
                <div
                  v-for="component in filteredChildren"
                  :id="component.id"
                  :key="component.id"
                  v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, component)"
                  :style="getComponentPositionStyle(component)"
                >
                  <LinkableComponent :id="component.id" no-border no-padding :used-in-section-id="usedInSectionId">
                    <Component
                      :is="componentMap[component.type as ComponentTypes]"
                      :parent-type="ComponentTypes.Scribble"
                      v-bind="omit(component, ['type', 'children', 'style'])"
                      :style="omit(component.style || {}, ['position', 'left', 'top', 'zIndex'])"
                      :used-in-section-id="usedInSectionId"
                      @style="(s: Record<string, any>) => emit('style', s, component.id)"
                    />
                  </LinkableComponent>
                </div>
              </div>
            </template>
          </div>
        </Draggable>
      </div>
    </EditableComponent>

    <div
      v-else
      ref="scrollableContainerRef"
      :class="{
        'overflow-x-hidden': !shouldShowHorizontalScroll,
        'overflow-x-scroll': shouldShowHorizontalScroll,
        'overflow-y-hidden': !isEditMode,
      }"
    >
      <div class="w-full h-full flex items-center justify-center">
        <div
          ref="containerRef"
          class="scribble-container !relative overflow-hidden bg-primary6"
          :class="{ 'scribble-container--covered': isEditMode && (fileNotFound || fileRestricted) }"
          :style="scaledContainerStyle"
        >
          <CSlideViewer
            v-if="showSlideViewer"
            class="absolute top-0 left-0 w-full h-full"
            :class="{ 'z-0': pdfReady, '-z-1': !pdfReady }"
            :page-index="computedPageIndex"
            :pdf-url="contentFileUrl!"
            @pdf-loaded="onPdfLoaded"
          />
          <a
            v-else-if="shouldShowReplaceLink"
            class="absolute top-0 left-0 w-full h-full flex items-center justify-center text-primary underline z-10"
            :href="replaceHref"
          >
            Replace
          </a>
          <CContentError
            v-else-if="isEditMode && (fileRestricted || fileNotFound)"
            v-bind="{
              restricted: fileRestricted,
              expired: fileNotFound && !fileRestricted,
            }"
          />
          <CErrorFullScreen v-else-if="fileNotFound || assetFileNotFound" v-bind="fileNotFoundErrorProps" />
          <div
            v-if="!shouldShowReplaceLink && filteredChildren.length"
            :class="{
              'absolute top-0 left-0 w-full h-full z-2': contentFileUrl,
            }"
            :style="childrenContainerStyle"
          >
            <div
              v-for="component in filteredChildren"
              :key="component.id"
              v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, component)"
              :style="getComponentPositionStyle(component)"
            >
              <LinkableComponent :id="component.id" no-border no-padding :used-in-section-id="usedInSectionId">
                <Component
                  :is="componentMap[component.type as ComponentTypes]"
                  :parent-type="ComponentTypes.Scribble"
                  v-bind="omit(component, ['type', 'children', 'style'])"
                  :style="omit(component.style || {}, ['position', 'left', 'top', 'zIndex'])"
                  :used-in-section-id="usedInSectionId"
                />
              </LinkableComponent>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { useElementSize } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { debounce, omit } from 'lodash-es'
import {
  toRefs,
  computed,
  ref,
  watch,
  type ComputedRef,
  type CSSProperties,
  onMounted,
  onBeforeUnmount,
  nextTick,
  inject,
} from 'vue'
import { VueDraggable as Draggable } from 'vue-draggable-plus'

import CButton from '@lib/components/CButton/CButton.vue'
import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CErrorFullScreen from '@lib/components/CError/CErrorFullScreen.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import CSlideViewer from '@lib/components/CSlideViewer/CSlideViewer.vue'
import {
  type ComponentTrackingParams,
  type ComponentNode,
  type ScribbleProps,
  ComponentTypes,
  type ComponentNodeThemeMeta,
  type CanvasTheme,
} from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { type CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme, { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import { calculateProportionalDimensions, ACTUAL_CANVAS_WIDTH, getComponentPositionStyle } from './scribble.helpers'
import { extractUrlFromCssBackgroundImage } from '@canvas-builder/util/url'
import { convertToPixels } from '@canvas-builder/util/print.util'
import type { BackgroundPositionX, BackgroundPositionY, BackgroundRepeat, BackgroundSize } from '@lib/types/background'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useReorderComponent from '@canvas-builder/composables/useReorderComponent'
import type { PitcherFile } from '@lib/types/file.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import CContentError from '@lib/components/CContentError/CContentError.vue'

// Initialize global cache and pending requests map
if (typeof window !== 'undefined') {
  if (!window.__scribbleFileCache) {
    window.__scribbleFileCache = new Map()
  }
  if (!window.__scribblePendingRequests) {
    window.__scribblePendingRequests = new Map()
  }
}

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const props = withDefaults(
  defineProps<{
    id: string
    data: ScribbleProps
    children?: ComponentNode[]
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: CSSProperties
    theme_meta?: ComponentNodeThemeMeta
  }>(),
  {
    children: () => [],
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties, id?: ComponentNode['id']]
}>()

const { id, usedInSectionId, when_used_in_section } = toRefs(props)

const { t } = useI18n()
const {
  componentNodesById,
  componentSelectedMode,
  duplicate,
  isEditMode,
  isDsr,
  isFullscreen,
  isPrintDisplayMode,
  isViewOnlyMode,
  mode,
  onComponentEnter,
  onComponentExit,
  removeComponentById,
  setAddMode,
  setComponentSelectedMode,
  setComponentSettingsMode,
  updateNodeDataById,
  activeCanvas,
  activeCanvasSections,
} = useCanvas()
const { isFluid } = useCanvasTheme()
const themeVars = useThemeVars()

// Add reorder component composable for isolation functionality
const { getFilteredScribbleChildren } = useReorderComponent()

const { getDerivedStyleFromFeatureFlags, getCssThemeMetaStyleOverrides } = useComponentStyle()

const sectionTheme = computed(() => {
  return usedInSectionId.value ? (activeCanvasSections.value[usedInSectionId.value]?.theme as CanvasTheme) : undefined
})

const fetchFileById =
  inject<(id: string, options?: { skip_error_toast?: boolean }) => Promise<PitcherFile> | null>('fetchFileById')

const componentMap = computed(() => {
  const baseComponents: Partial<Record<ComponentTypes, any>> = {
    Multimedia,
    Text,
  }

  if (launchDarkly.value?.allow_dynamic_data_table_for_scribble_component) {
    baseComponents.DynamicDataTable = DynamicDataTable
  }

  if (launchDarkly.value?.allow_html_for_scribble_component) {
    baseComponents.Html = Html
  }

  if (launchDarkly.value?.allow_embeddable_for_scribble_component) {
    baseComponents.Embeddable = Embeddable
  }

  return baseComponents
})

const computedPageIndex = computed(() => {
  if (props.data?.content_background?.page_id) {
    const pageIndex = contentFileMetadataPageInfo.value.findIndex(
      (section: any) => section.id === props.data?.content_background?.page_id,
    )
    return pageIndex
  }

  return props.data?.content_background?.page_index ?? 0
})

const showSlideViewer = computed(
  () =>
    // In print mode, never show PDF content - always use background image
    !isPrintDisplayMode.value &&
    !isPitcherWkWebView &&
    !!contentFileUrl.value &&
    !fileNotFound.value &&
    computedPageIndex.value !== -1,
)

const showError = computed(() => {
  // Show error for content background issues
  const contentError = !isPdfLoading.value && (fileNotFound.value || computedPageIndex.value === -1)
  // Show error for asset background issues (when is_deleted is true)
  const assetError = assetFileNotFound.value

  return contentError || assetError
})

const fileNotFoundErrorProps = computed(() => ({
  class: 'absolute top-0 left-0 w-full h-full z-0',
  description: t('canvasUI.canvasBuilder.scribble.fileNotFound.description'),
  fitParent: true,
  icon: 'image-slash',
  title: t('canvasUI.canvasBuilder.scribble.fileNotFound.title'),
}))

const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const scribbleRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const scrollableContainerRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const dropPreviewRef = ref<HTMLDivElement | null>(null)
const dropPosition = ref<{ x: number; y: number } | null>(null)
const isDraggingInside = ref(false)
const dragOffset = ref<{ x: number; y: number } | null>(null)
const contentFileUrl = ref<PitcherFile['content_url']>(null)
const contentFileOriginalUrl = ref<PitcherFile['original_url']>(null)
const contentFileMetadataPageInfo = ref<PitcherFile['metadata']>([])
const pdfReady = ref(false)
const fileNotFound = ref(false)
const fileRestricted = ref(false)
const isPdfLoading = ref(false)
const assetFileNotFound = ref(false)
const defaultBackgroundStyles: {
  backgroundPositionX: BackgroundPositionX
  backgroundPositionY: BackgroundPositionY
  backgroundSize: BackgroundSize
  backgroundRepeat: BackgroundRepeat
} = {
  backgroundPositionX: 'left',
  backgroundPositionY: 'top',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
}

const { width: scribbleWidth } = useElementSize(scribbleRef)

// Track window height for responsive calculations
const windowHeight = ref(window.innerHeight)

// Helper function to safely parse CSS height values
function getSafeElementHeight(element: Element | null): number {
  if (!element) return 0

  try {
    const heightStr = window.getComputedStyle(element).getPropertyValue('height')
    const height = parseFloat(heightStr)

    // Return 0 if parsing failed or resulted in NaN/negative values
    return isNaN(height) || height < 0 ? 0 : height
  } catch (error) {
    console.warn('Error getting element height:', error)
    return 0
  }
}

// Calculate available height dynamically
const availableHeight = computed(() => {
  if (!scribbleRef.value) return 0

  const toolbarElement =
    isDsr.value || isFullscreen.value
      ? document.querySelector('.canvas-builder--progress-bar')
      : (document.getElementById('slotty-ui-header') ?? document.querySelector('.p-app-bar'))

  const topBarHeight = getSafeElementHeight(toolbarElement)

  return windowHeight.value - topBarHeight
})

// Add scaling logic for presentation mode
const scaleFactor = computed(() => {
  // Check for print display mode with PDF dimensions
  if (isPrintDisplayMode.value && launchDarkly.value.enable_scale_content) {
    // Use canvas-specific PDF dimensions if set, otherwise fall back to pitcher settings default
    const canvasPdfWidth =
      launchDarkly.value.enable_canvas_pdf_dimensions && activeCanvas.value?.theme?.pdf_dimensions?.width
    const canvasPdfHeight =
      launchDarkly.value.enable_canvas_pdf_dimensions && activeCanvas.value?.theme?.pdf_dimensions?.height

    const pdfWidth = canvasPdfWidth || pitcherSettings.value.default_pdf_page_dimensions?.width
    const pdfHeight = canvasPdfHeight || pitcherSettings.value.default_pdf_page_dimensions?.height

    // Check for manual print_scale override (only from pitcher settings, not canvas-specific)
    if (
      !canvasPdfWidth &&
      !canvasPdfHeight &&
      pdfWidth &&
      pdfHeight &&
      pitcherSettings.value.default_pdf_page_dimensions?.print_scale
    ) {
      return pitcherSettings.value.default_pdf_page_dimensions.print_scale
    }

    if (pdfWidth && pdfHeight) {
      const pdfWidthPx = convertToPixels(pdfWidth)
      const pdfHeightPx = convertToPixels(pdfHeight)

      if (pdfWidthPx && pdfHeightPx && props.style?.width && props.style?.height) {
        const scribbleWidthPx = parseInt(props.style.width as string)
        const scribbleHeightPx = parseInt(props.style.height as string)

        // Calculate scale factors for both dimensions to match PDF page dimensions
        // This will scale up smaller slides and scale down larger slides
        const widthScale = pdfWidthPx / scribbleWidthPx
        const heightScale = pdfHeightPx / scribbleHeightPx
        // Use the smaller scale factor to ensure both dimensions fit proportionally within PDF page
        return Math.min(widthScale, heightScale)
      }
    }
  }

  // Only scale in presentation mode when scribble would overflow
  if (isEditMode.value || !scribbleRef.value || !props.style?.width || !props.style?.height) return 1

  const scribbleWidthPx = parseInt(props.style.width as string)
  const scribbleHeightPx = parseInt(props.style.height as string)
  const availableWidth = scribbleWidth.value

  // If scribble fits within available dimensions, no scaling needed
  if (
    (isNaN(scribbleWidthPx) || scribbleWidthPx <= availableWidth) &&
    (isNaN(scribbleHeightPx) || scribbleHeightPx <= availableHeight.value)
  ) {
    return 1
  }

  // Calculate scale factors for both dimensions
  const widthScale = availableWidth / scribbleWidthPx
  const heightScale = availableHeight.value / scribbleHeightPx

  // Use the smaller scale factor to ensure both dimensions fit without scrollbars
  // This maintains aspect ratio while preventing overflow in either dimension
  return Math.min(widthScale, heightScale)
})

// Check if we should center content in fluid mode due to height constraints
const shouldCenterInFluidMode = computed(() => {
  if (isEditMode.value || !isFluid.value) return false
  if (!props.style?.backgroundImage || !scribbleWidth.value || !availableHeight.value) return false

  const originalWidth = parseInt((props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}`)
  const originalHeight = parseInt((props.style?.height as string) || '500')
  const widthScale = scribbleWidth.value / originalWidth
  const scaledHeight = Math.round(originalHeight * widthScale)

  // Return true if the height was constrained by available space
  return scaledHeight > availableHeight.value
})

const childrenContainerStyle = computed(() => {
  // Calculate fluid scaling factor for children positioning
  const fluidScaleFactor = computed(() => {
    if (isEditMode.value || !isFluid.value || !scribbleWidth.value) return 1

    // Only apply fluid scaling when we're actually using dynamic width in containerStyle
    // This matches the condition in containerStyle for dynamic width
    if (!props.style?.backgroundImage) return 1

    // Calculate the original width that children positions were based on
    const originalWidth = parseInt((props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}`)

    // In fluid mode with background image, scale children positions based on available width vs original width
    return scribbleWidth.value / originalWidth
  })

  // Calculate fluid height scaling factor for children positioning when height is constrained
  const fluidHeightScaleFactor = computed(() => {
    if (isEditMode.value || !isFluid.value || !shouldCenterInFluidMode.value) return 1
    if (!props.style?.backgroundImage) return 1

    const originalWidth = parseInt((props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}`)
    const originalHeight = parseInt((props.style?.height as string) || '500')
    const widthScale = scribbleWidth.value / originalWidth
    const scaledHeight = Math.round(originalHeight * widthScale)

    // If height was constrained, calculate the final scale factor that was applied
    if (scaledHeight > availableHeight.value && availableHeight.value > 0) {
      const heightScale = availableHeight.value / originalHeight
      return Math.min(widthScale, heightScale)
    }

    return widthScale
  })

  const cssTransformScale = scaleFactor.value
  const fluidScale = fluidScaleFactor.value
  const fluidHeightScale = fluidHeightScaleFactor.value

  // Use the appropriate scaling:
  // 1. CSS transform scaling for overflow scenarios (non-fluid) and print mode
  // 2. Fluid height scaling when height is constrained in fluid mode
  // 3. Fluid scaling for background image width expansion
  let finalScale = 1
  if (cssTransformScale !== 1) {
    finalScale = cssTransformScale
  } else if (shouldCenterInFluidMode.value) {
    finalScale = fluidHeightScale
  } else if (fluidScale !== 1) {
    finalScale = fluidScale
  }

  if (finalScale === 1) return {}

  // Apply transform scale to children container in presentation mode
  return {
    transform: `scale(${finalScale})`,
    transformOrigin: 'top left',
    width: `${parseInt((props.style?.width as string) || '0')}px`,
    height: `${parseInt((props.style?.height as string) || '500')}px`,
  }
})

const scaledContainerStyle = computed(() => {
  const scale = scaleFactor.value
  if (scale === 1) return containerStyle.value

  // In presentation mode or print mode with scaling, adjust outer container dimensions
  const originalWidth = parseInt((props.style?.width as string) || '0')
  const originalHeight = parseInt((props.style?.height as string) || '500')

  let scaledStyle: Record<string, any> = {
    ...containerStyle.value,
    width: `${originalWidth * scale}px`,
    height: `${originalHeight * scale}px`,
    maxWidth: undefined, // Remove maxWidth when scaling
    // Remove margin properties since we're using flexbox centering in the wrapper
    marginLeft: undefined,
    marginRight: undefined,
    marginTop: undefined,
    marginBottom: undefined,
  }

  // For print mode, ensure the container is centered within the PDF page dimensions
  if (
    isPrintDisplayMode.value &&
    launchDarkly.value.enable_scale_content &&
    pitcherSettings.value.default_pdf_page_dimensions?.width &&
    pitcherSettings.value.default_pdf_page_dimensions?.height
  ) {
    const pdfWidthPx = convertToPixels(pitcherSettings.value.default_pdf_page_dimensions.width)
    const pdfHeightPx = convertToPixels(pitcherSettings.value.default_pdf_page_dimensions.height)

    if (pdfWidthPx && pdfHeightPx) {
      const scaledWidth = originalWidth * scale
      const scaledHeight = originalHeight * scale

      // Add centering margins for print mode
      scaledStyle = {
        ...scaledStyle,
        marginLeft: `${Math.max(0, (pdfWidthPx - scaledWidth) / 2)}px`,
        marginRight: `${Math.max(0, (pdfWidthPx - scaledWidth) / 2)}px`,
        marginTop: `${Math.max(0, (pdfHeightPx - scaledHeight) / 2)}px`,
        marginBottom: `${Math.max(0, (pdfHeightPx - scaledHeight) / 2)}px`,
      }
    }
  }

  return scaledStyle
})

const activeChild = computed(() =>
  componentSelectedMode.value ? (componentNodesById.value[componentSelectedMode.value.id] as ComponentNode) : null,
)

const componentNode = computed(() => (props.id ? componentNodesById.value[props.id] : null) as ComponentNode)

// Filter children based on isolation state
const filteredChildren = computed(() => getFilteredScribbleChildren(props.id, props.children))

const shouldShowEmptyState = computed(
  () =>
    !props.children?.length &&
    !props.style?.backgroundImage &&
    !contentFileUrl.value &&
    !fileNotFound.value &&
    !assetFileNotFound.value &&
    computedPageIndex.value !== -1,
)

const { createStyleUpdate: createActiveChildStyleUpdate } = useComponentStyle(activeChild as ComputedRef<ComponentNode>)
const { createStyleUpdate: createComponentNodeStyleUpdate } = useComponentStyle(componentNode)

const containerStyle = computed(() => {
  // Calculate dynamic width and height for fluid mode in presentation
  let dynamicWidth: string
  let dynamicHeight: string

  if (
    !props.children?.length &&
    !props.style?.backgroundImage &&
    !contentFileUrl.value &&
    !fileNotFound.value &&
    !assetFileNotFound.value
  ) {
    dynamicWidth = 'auto'
    dynamicHeight = 'auto'
  } else if (isFluid.value && !isEditMode.value && props.style?.backgroundImage && scribbleWidth.value > 0) {
    // In fluid presentation mode with background image, use the full available width
    const originalWidth = parseInt((props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}`)
    const originalHeight = parseInt((props.style?.height as string) || '500')

    // Calculate proportional height based on the width scaling
    const widthScale = scribbleWidth.value / originalWidth
    const scaledHeight = Math.round(originalHeight * widthScale)

    // Check if the scaled height would overflow the available height
    if (scaledHeight > availableHeight.value && availableHeight.value > 0) {
      // If height would overflow, scale both dimensions to fit within available height
      const heightScale = availableHeight.value / originalHeight
      const finalScale = Math.min(widthScale, heightScale)

      dynamicWidth = `${Math.round(originalWidth * finalScale)}px`
      dynamicHeight = `${Math.round(originalHeight * finalScale)}px`
    } else {
      // Use width-based scaling if height fits
      dynamicWidth = `${scribbleWidth.value}px`
      dynamicHeight = `${scaledHeight}px`
    }
  } else {
    // Default behavior: use style width/height or fallback to defaults
    dynamicWidth = (props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}px`
    dynamicHeight =
      !props.children?.length &&
      !props.style?.height &&
      !contentFileUrl.value &&
      !fileNotFound.value &&
      !assetFileNotFound.value
        ? 'auto'
        : (props.style?.height as string) || '500px'
  }

  // Determine if we should show background image
  // Show background image when PSPDFKit is not ready (for smooth transition)
  const shouldShowBackgroundImage = computed(() => {
    // If we have a PDF viewer, show background image until PDF is ready
    if (showSlideViewer.value) {
      return !pdfReady.value
    }

    if (
      !isPrintDisplayMode.value ||
      (props.style?.backgroundImage &&
        !props.style?.backgroundImage.includes('thumbnail') &&
        !props.style?.backgroundImage.includes('.webp.webp?v='))
    ) {
      return !pdfReady.value
    }
    if (delayBackgroundImageLoad.value) {
      // Only show background if we have a PDF file and children (which makes shouldShowReplaceLink false)
      return contentFileUrl.value && props.children?.length > 0
    }
    // Otherwise, show background image unless shouldShowReplaceLink is true
    return !shouldShowReplaceLink.value
  })

  // Base style without theme meta overrides
  const baseStyle = {
    backgroundColor: shouldShowReplaceLink.value ? 'transparent' : props.style?.backgroundColor,
    backgroundImage: shouldShowBackgroundImage.value ? props.style?.backgroundImage : 'none',
    backgroundPositionX: shouldShowBackgroundImage.value ? props.style?.backgroundPositionX : undefined,
    backgroundPositionY: shouldShowBackgroundImage.value ? props.style?.backgroundPositionY : undefined,
    backgroundRepeat: shouldShowBackgroundImage.value ? props.style?.backgroundRepeat : undefined,
    backgroundSize: shouldShowBackgroundImage.value ? props.style?.backgroundSize : undefined,
    height: dynamicHeight,
    marginLeft: isFluid.value ? 'auto' : undefined,
    marginRight: isFluid.value ? 'auto' : undefined,
    maxWidth:
      isFluid.value &&
      !props.children?.length &&
      !props.style?.backgroundImage &&
      !contentFileUrl.value &&
      !fileNotFound.value &&
      !assetFileNotFound.value
        ? `${ACTUAL_CANVAS_WIDTH}px`
        : undefined,
    width: dynamicWidth,
  }

  // Apply feature flags and theme meta style overrides
  return {
    ...getDerivedStyleFromFeatureFlags(baseStyle),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Scribble, props.theme_meta, sectionTheme.value),
  }
})

const shouldShowHorizontalScroll = computed(() => {
  if (!scribbleRef.value || !props.style?.width) return false
  const styleWidth = parseInt(props.style.width as string)

  // In presentation mode with scaling, never show horizontal scroll
  if (!isEditMode.value && scaleFactor.value < 1) return false

  return !isNaN(styleWidth) && styleWidth > scribbleWidth.value
})

const shouldShowReplaceLink = computed(
  () =>
    isPrintDisplayMode.value &&
    launchDarkly.value?.enable_original_pdf_link &&
    launchDarkly.value?.enable_scale_content &&
    contentFileUrl.value &&
    !props.children?.length &&
    computedPageIndex.value !== -1,
)

const replaceHref = computed(() => {
  // Ensure contentFileUrl exists (shouldShowReplaceLink already checks this, but be defensive)
  if (!contentFileUrl.value) return '#'

  let href = `${contentFileUrl.value}|${computedPageIndex.value + 1}`

  // Only append original URL if all conditions are met and values are valid
  if (
    launchDarkly.value?.keep_static_pptx_editable &&
    contentFileOriginalUrl.value &&
    contentFileOriginalUrl.value.toLowerCase().includes('.pptx')
  ) {
    href += `|${contentFileOriginalUrl.value}`
  }

  return href
})

const delayBackgroundImageLoad = computed(
  () =>
    isPrintDisplayMode.value &&
    launchDarkly.value?.enable_original_pdf_link &&
    launchDarkly.value?.enable_scale_content,
)

const dropPreviewStyle: ComputedRef<CSSProperties> = computed(() => {
  if (!dropPosition.value) return { display: 'none' }

  // Get dimensions from the active child component's style
  let previewWidth
  let previewHeight

  if (activeChild.value) {
    const childComponent = componentNodesById.value[activeChild.value.id]
    if (childComponent?.style) {
      previewWidth = childComponent.style.width
      previewHeight = childComponent.style.height
    }

    // Fallback: get dimensions from DOM element if style didn't provide valid dimensions
    if (!previewWidth || !previewHeight || previewWidth === 'auto' || previewHeight === 'auto') {
      const element = document.getElementById(activeChild.value.id)
      if (element && element.firstElementChild) {
        const rect = element.firstElementChild.getBoundingClientRect()
        if (rect.width > 0) previewWidth = `${rect.width}px`
        if (rect.height > 0) previewHeight = `${rect.height}px`
      }
    }

    if (!previewWidth) {
      previewWidth = '200px'
    }

    if (!previewHeight) {
      previewHeight = '80px'
    }
  }

  // Apply drag offset to position the preview relative to where user clicked
  let adjustedX = dropPosition.value.x
  let adjustedY = dropPosition.value.y

  if (dragOffset.value) {
    adjustedX = dropPosition.value.x - dragOffset.value.x
    adjustedY = dropPosition.value.y - dragOffset.value.y
  }

  return {
    position: 'absolute',
    left: `${adjustedX}px`,
    top: `${adjustedY}px`,
    pointerEvents: 'none',
    zIndex: 1000,
    opacity: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    border: `2px dashed ${themeVars.value.primary}`,
    width: previewWidth,
    height: previewHeight,
    borderRadius: '4px',
  }
})

const DRAG_THROTTLE = 2
const KEYBOARD_POSITION_ADJUSTMENT_STEP = 1 // 1px movement per key press
const KEYBOARD_POSITION_ADJUSTMENT_STEP_LARGE = 10 // 10px movement for Shift+arrow
const WINDOW_RESIZE_DEBOUNCE_TIME = 50

const debouncedUpdateDropPosition = debounce((clientX: number, clientY: number, container: HTMLElement) => {
  const containerBounds = container.getBoundingClientRect()
  dropPosition.value = {
    x: clientX - containerBounds.left,
    y: clientY - containerBounds.top,
  }
}, DRAG_THROTTLE)

function centerScrollPosition() {
  if (!scrollableContainerRef.value || !containerRef.value || !shouldShowHorizontalScroll.value) return

  const containerWidth = parseInt((props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}`)
  const scrollableWidth = scrollableContainerRef.value.clientWidth

  if (containerWidth > scrollableWidth) {
    const scrollOffset = (containerWidth - scrollableWidth) / 2
    scrollableContainerRef.value.scrollLeft = scrollOffset
  }
}

const debouncedCenterScrollPosition = debounce(centerScrollPosition, WINDOW_RESIZE_DEBOUNCE_TIME)

function updateWindowHeight() {
  windowHeight.value = window.innerHeight
}

const debouncedUpdateWindowHeight = debounce(updateWindowHeight, WINDOW_RESIZE_DEBOUNCE_TIME)

function clearErrorStates() {
  assetFileNotFound.value = false
  fileNotFound.value = false
  fileRestricted.value = false
  contentFileUrl.value = null
  contentFileOriginalUrl.value = null
  contentFileMetadataPageInfo.value = []
}

function clearState() {
  isDragging.value = false
  isDraggingInside.value = false
  dropPosition.value = null
  dragOffset.value = null
  debouncedUpdateDropPosition.cancel() // Cancel any pending updates
}

function onAdd(componentId?: ComponentNode['id']) {
  if (componentSelectedMode.value && componentSelectedMode.value?.id === componentId) {
    setAddMode(ComponentTypes.Scribble, componentId)
  } else {
    setComponentSelectedMode(componentId)
  }
}

function onDragLeave() {
  isDraggingInside.value = false
  dropPosition.value = null
}

function onDragStart(event: DragEvent) {
  if (!event.dataTransfer) return
  isDragging.value = true

  // Calculate the offset from where the user clicked within the component
  if (activeChild.value && containerRef.value) {
    const childComponent = componentNodesById.value[activeChild.value.id]
    if (childComponent?.style) {
      const containerRect = containerRef.value.getBoundingClientRect()

      // Get the component's current position from its style
      const componentLeft = parseInt(childComponent.style.left as string) || 0
      const componentTop = parseInt(childComponent.style.top as string) || 0

      // Calculate click position relative to the container
      const clickInContainer = {
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
      }

      // Calculate offset within the child component
      const clickOffsetX = clickInContainer.x - componentLeft
      const clickOffsetY = clickInContainer.y - componentTop

      dragOffset.value = { x: clickOffsetX, y: clickOffsetY }
    }
  }

  const dragImage = document.createElement('div')
  dragImage.style.width = '100px'
  dragImage.style.height = '100px'
  dragImage.style.opacity = '0'
  document.body.appendChild(dragImage)

  event.dataTransfer.setDragImage(dragImage, 0, 0)

  setTimeout(() => {
    document.body.removeChild(dragImage)
  }, 0)
}

function onDragOver(event: DragEvent) {
  if (!containerRef.value || !event.clientX || !event.clientY) return
  event.preventDefault()

  isDraggingInside.value = true
  debouncedUpdateDropPosition(event.clientX, event.clientY, containerRef.value)
}

function onElementVisibility(isVisible: boolean, c: ComponentNode) {
  const params: ComponentTrackingParams = { trackable: false }
  if (isVisible) onComponentEnter.value?.(c, params)
  else onComponentExit.value?.(c, params)
}

function onMouseDown(e: MouseEvent, componentId: ComponentNode['id']) {
  if (e.button !== 0) return
  onAdd(componentId)
}

function onPdfLoaded() {
  isPdfLoading.value = false
  // Immediately mark as ready for instant transition
  pdfReady.value = true
}

function onDrop(event: DragEvent) {
  if (!containerRef.value || !event.clientX || !event.clientY || !isDraggingInside.value) {
    clearState()
    return
  }

  const containerBounds = containerRef.value.getBoundingClientRect()
  let left = event.clientX - containerBounds.left
  let top = event.clientY - containerBounds.top

  // Apply drag offset to final drop position
  if (dragOffset.value) {
    left = left - dragOffset.value.x
    top = top - dragOffset.value.y
  }

  if (activeChild.value && props.children?.find((child) => child.id === activeChild.value?.id)) {
    emit(
      'style',
      createActiveChildStyleUpdate({
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
      }),
      activeChild.value.id,
    )
  }

  clearState()
}

function handleKeyboardNavigation(event: KeyboardEvent) {
  // Only process if in edit mode and not in view-only mode
  if (!isEditMode.value || isViewOnlyMode.value) return

  // Only process if we have a selected component that's a child of this scribble
  if (!componentSelectedMode.value || !activeChild.value) return
  if (!props.children?.find((child) => child.id === activeChild.value?.id)) return

  // Only handle arrow keys
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return

  // Skip if the active element is an input, textarea, or contenteditable
  // This prevents conflicts with text editing in the rich text editor
  const activeElement = document.activeElement
  if (
    activeElement instanceof HTMLInputElement ||
    activeElement instanceof HTMLTextAreaElement ||
    activeElement?.getAttribute('contenteditable') === 'true' ||
    activeElement?.closest('[contenteditable="true"]')
  ) {
    return
  }

  // Prevent default behavior and stop propagation to other containers
  // Use capture phase to intercept the event before it reaches other handlers
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()

  const currentStyle = activeChild.value.style || {}
  const currentLeft = parseInt((currentStyle.left as string) || '0')
  const currentTop = parseInt((currentStyle.top as string) || '0')

  const step = event.shiftKey ? KEYBOARD_POSITION_ADJUSTMENT_STEP_LARGE : KEYBOARD_POSITION_ADJUSTMENT_STEP

  let newLeft = currentLeft
  let newTop = currentTop

  switch (event.key) {
    case 'ArrowUp':
      newTop -= step
      break
    case 'ArrowDown':
      newTop += step
      break
    case 'ArrowLeft':
      newLeft -= step
      break
    case 'ArrowRight':
      newLeft += step
      break
  }

  emit(
    'style',
    createActiveChildStyleUpdate({
      position: 'absolute',
      left: `${newLeft}px`,
      top: `${newTop}px`,
    }),
    activeChild.value.id,
  )
}

onMounted(async () => {
  if (props.data?.content_background?.file_id) {
    if (props.data.content_background.is_deleted) {
      fileNotFound.value = true
    } else {
      await fetchFile(props.data.content_background.file_id)
    }
  }

  if (props.data?.asset_background?.is_deleted) {
    assetFileNotFound.value = true
  }

  window.addEventListener('keydown', handleKeyboardNavigation, true)

  // initially, center scroll position
  nextTick(() => {
    debouncedCenterScrollPosition()
  })

  window.addEventListener('resize', debouncedCenterScrollPosition)
  window.addEventListener('resize', debouncedUpdateWindowHeight)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyboardNavigation, true)
  window.removeEventListener('resize', debouncedCenterScrollPosition)
  window.removeEventListener('resize', debouncedUpdateWindowHeight)
  debouncedCenterScrollPosition.cancel() // Cancel any pending debounced calls
  debouncedUpdateWindowHeight.cancel() // Cancel any pending debounced calls
})

async function fetchFile(fileId: string) {
  if (launchDarkly.value.enable_scribble_content_as_background && fetchFileById) {
    // Check cache first
    if (window.__scribbleFileCache?.has(fileId)) {
      const cached = window.__scribbleFileCache.get(fileId)!
      contentFileUrl.value = cached.contentUrl
      contentFileOriginalUrl.value = cached.originalUrl
      contentFileMetadataPageInfo.value = cached.metadataPageInfo
      fileNotFound.value = cached.fileNotFound
      fileRestricted.value = !!cached.fileRestricted
      isPdfLoading.value = false
      return
    }

    // Check if there's already a pending request for this fileId
    if (window.__scribblePendingRequests?.has(fileId)) {
      const pendingPromise = window.__scribblePendingRequests.get(fileId)
      if (pendingPromise) {
        isPdfLoading.value = true
        try {
          const file = await pendingPromise
          // Process the result
          if ((file as any)?.error) {
            contentFileUrl.value = null
            contentFileOriginalUrl.value = null
            contentFileMetadataPageInfo.value = []
            fileNotFound.value = true
            fileRestricted.value = (file as any)?.error?.status === 403
          } else {
            contentFileUrl.value = file?.content_url ?? null
            contentFileOriginalUrl.value = file?.original_url ?? null
            contentFileMetadataPageInfo.value = (file as any)?.metadata?.pptx_data?.slides ?? []
            fileNotFound.value = false
          }
        } catch (error) {
          console.error('Error fetching file from pending request:', error)
          contentFileUrl.value = null
          contentFileOriginalUrl.value = null
          contentFileMetadataPageInfo.value = []
          fileNotFound.value = true
          fileRestricted.value = (error as any)?.status === 403
        } finally {
          isPdfLoading.value = false
        }
        return
      }
    }

    // Create new request
    isPdfLoading.value = true

    const fetchPromise = fetchFileById(fileId, { skip_error_toast: true })

    // Store the pending promise
    window.__scribblePendingRequests?.set(fileId, fetchPromise as Promise<PitcherFile>)

    try {
      const file = await fetchPromise

      // Cache the result
      const cacheData = {
        contentUrl: null as string | null,
        originalUrl: null as string | null,
        metadataPageInfo: [] as any[],
        fileNotFound: false,
        fileRestricted: false,
      }

      // handle dsr case
      if ((file as any)?.error) {
        cacheData.fileNotFound = true
        contentFileUrl.value = null
        contentFileOriginalUrl.value = null
        contentFileMetadataPageInfo.value = []
        fileNotFound.value = true
        const isRestricted = (file as any)?.error?.status === 403
        cacheData.fileRestricted = isRestricted
        fileRestricted.value = isRestricted
      } else {
        cacheData.contentUrl = file?.content_url ?? null
        cacheData.originalUrl = file?.original_url ?? null
        cacheData.metadataPageInfo = (file as any)?.metadata?.pptx_data?.slides ?? []
        contentFileUrl.value = cacheData.contentUrl
        contentFileOriginalUrl.value = cacheData.originalUrl
        contentFileMetadataPageInfo.value = cacheData.metadataPageInfo
        fileNotFound.value = false
      }

      // Store in cache
      window.__scribbleFileCache?.set(fileId, cacheData)
    } catch (error) {
      console.error('Error fetching file:', error)

      const isRestricted = (error as any)?.status === 403

      // Cache the error state
      const errorCacheData = {
        contentUrl: null,
        originalUrl: null,
        metadataPageInfo: [],
        fileNotFound: true,
        fileRestricted: isRestricted,
      }
      window.__scribbleFileCache?.set(fileId, errorCacheData)

      contentFileUrl.value = null
      contentFileOriginalUrl.value = null
      contentFileMetadataPageInfo.value = []
      fileNotFound.value = true
      fileRestricted.value = isRestricted
    } finally {
      // Remove from pending requests
      window.__scribblePendingRequests?.delete(fileId)
      isPdfLoading.value = false
    }
  } else {
    contentFileUrl.value = null
    contentFileOriginalUrl.value = null
    contentFileMetadataPageInfo.value = []
    fileNotFound.value = false
    isPdfLoading.value = false
  }
}

async function updateNodeDataForAsset({ assetId }: { assetId: string }) {
  // Clear content background data when asset is selected
  const { content_background, ...newData } = props.data
  updateNodeDataById(props.id, {
    ...newData,
    asset_background: {
      asset_id: assetId,
    },
  })

  // Reset all error states when a valid asset is selected
  clearErrorStates()
}

async function updateNodeDataForContent({
  fileId,
  pageIndex,
  pageId,
  thumbnailUrl,
}: {
  fileId: string
  pageIndex?: number
  pageId?: string
  thumbnailUrl?: string
}) {
  // If fileId is empty, clear the content_background entirely
  if (!fileId) {
    const { content_background, asset_background, ...newData } = props.data
    updateNodeDataById(props.id, newData)
    clearErrorStates()
    return
  }

  // Clear asset background data when content is selected
  const { asset_background, ...newData } = props.data
  updateNodeDataById(props.id, {
    ...newData,
    content_background: {
      file_id: fileId,
      page_index: pageIndex,
      page_id: pageId,
    },
  })

  // Reset asset error state when content is selected
  assetFileNotFound.value = false

  await fetchFile(fileId)

  // Clear background image properties when content file is selected
  if (props.style?.backgroundImage) {
    emit(
      'style',
      createComponentNodeStyleUpdate({
        backgroundImage: null,
        backgroundPositionX: undefined,
        backgroundPositionY: undefined,
        backgroundRepeat: undefined,
        backgroundSize: undefined,
      }),
    )
  }

  // Set the PDF thumbnail as background image to leverage existing dimension calculation
  if (thumbnailUrl) {
    emit(
      'style',
      createComponentNodeStyleUpdate({
        backgroundImage: thumbnailUrl,
      }),
    )
  }
}

function handleEditableComponentStyleEvent(s: CSSProperties) {
  // Check if the background image has changed and has a value
  if (s.backgroundImage && s.backgroundImage !== props.style?.backgroundImage) {
    // Clear the content_background from the scribble data
    clearErrorStates()
    const { content_background, asset_background, ...newData } = props.data
    updateNodeDataById(props.id, newData)
  }

  // Continue with the normal style event emission
  emit('style', s)
}

// Helper function to update scribble dimensions based on background image
async function updateScribbleDimensionsFromBackgroundImage() {
  if (!props.style?.backgroundImage) return

  const imageUrl = extractUrlFromCssBackgroundImage(props.style.backgroundImage) ?? ''
  const img = new Image()
  img.src = imageUrl

  try {
    await new Promise((resolve, reject) => {
      img.onload = () => {
        // In edit mode, always use ACTUAL_CANVAS_WIDTH
        if (isEditMode.value) {
          const { width, height } = calculateProportionalDimensions(
            img.naturalWidth,
            img.naturalHeight,
            ACTUAL_CANVAS_WIDTH,
          )
          emit(
            'style',
            createComponentNodeStyleUpdate({
              ...defaultBackgroundStyles,
              height: `${height}px`,
              width: `${width}px`,
            }),
          )
        } else if (isFluid.value && scribbleWidth.value > 0) {
          // In fluid presentation mode, scale based on available space vs image natural dimensions
          // Calculate what the image width would be if scaled to ACTUAL_CANVAS_WIDTH
          const { width: standardWidth } = calculateProportionalDimensions(
            img.naturalWidth,
            img.naturalHeight,
            ACTUAL_CANVAS_WIDTH,
          )

          // If we have more space available than the standard width, scale up using available space
          // Otherwise, use the standard width (which might still scale down if scribbleWidth < ACTUAL_CANVAS_WIDTH)
          const shouldScaleUp = scribbleWidth.value > standardWidth
          const targetWidth = shouldScaleUp ? scribbleWidth.value : ACTUAL_CANVAS_WIDTH

          const { width, height } = calculateProportionalDimensions(img.naturalWidth, img.naturalHeight, targetWidth)

          emit(
            'style',
            createComponentNodeStyleUpdate({
              ...defaultBackgroundStyles,
              height: `${height}px`,
              width: `${width}px`,
            }),
          )
        } else {
          // Non-fluid presentation mode or feature flag disabled, use ACTUAL_CANVAS_WIDTH
          const { width, height } = calculateProportionalDimensions(
            img.naturalWidth,
            img.naturalHeight,
            ACTUAL_CANVAS_WIDTH,
          )
          emit(
            'style',
            createComponentNodeStyleUpdate({
              ...defaultBackgroundStyles,
              height: `${height}px`,
              width: `${width}px`,
            }),
          )
        }

        resolve(null)
      }
      img.onerror = reject
    })
  } catch (error) {
    console.error('Error loading image:', error)
  }
}

watch(
  () => props.data?.asset_background?.is_deleted,
  (isDeleted) => {
    if (!isDeleted) return
    assetFileNotFound.value = true
  },
)

watch(
  () => props.style?.backgroundImage,
  async (newBackgroundImage, oldBackgroundImage) => {
    if (!newBackgroundImage || newBackgroundImage === oldBackgroundImage) return
    await updateScribbleDimensionsFromBackgroundImage()
  },
)

// Watch for scribble width changes in fluid mode to recalculate dimensions
watch([() => scribbleWidth.value, () => isFluid.value, () => isEditMode.value], async () => {
  if (isFluid.value && !isEditMode.value && props.style?.backgroundImage) {
    await updateScribbleDimensionsFromBackgroundImage()
  }
})

// Reset ready state when slide viewer is hidden
watch(
  () => showSlideViewer.value,
  (newValue) => {
    if (!newValue) {
      pdfReady.value = false
    }
  },
)
</script>

<style scoped lang="scss">
.ghost-class {
  opacity: 0.8;
  position: absolute;
  pointer-events: none;
  z-index: 1000;
}

.ghost-class * {
  pointer-events: none;
}

.scribble-container--covered {
  background-color: rgba(255, 255, 255, 0.95);
  background-blend-mode: lighten;
}
</style>
